// MiniMap.jsx
// Draggable minimap component for displaying a small overview of the main canvas

// Copyright HS Analysis GmbH, 2019
// Author: Sebastian Murgul, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

// External imports
import autoBind from "react-autobind";
import Draggable from "react-draggable";

// Material-UI imports
import IconButton from "@mui/material/IconButton";
import withStyles from "@mui/styles/withStyles";
import withTheme from "@mui/styles/withTheme";

// Icon imports
import DragIndicator from "@mui/icons-material/DragIndicator";

// HSA imports
import { getColorMapColor } from "../utils/Constants";
import { trackTransforms } from "../utils/CanvasUtil";
import { withTiles } from "../contexts/TilesContext";
import HSAIcon from "../../common/utils/HSAIcon";

const styles = {
  root: {
    position: "absolute",
    left: 5,
    height: 170,
    width: 220,
    border: "2px solid rgb(85, 85, 85)",
    opacity: 0.8,
  },
  rootsidebar: {
    height: 170,
    width: 220,
    border: "2px solid rgb(85, 85, 85)",
    opacity: 0.8,
  },
  canvas: {
    position: "relative",
    width: "100%",
    height: "100%",
    backgroundColor: "#000000",
  },
  dragIndicator: {
    color: "#fff",
    position: "absolute",
    top: 5,
    right: 5,
    cursor: "grab",
  },
  grabbing: {
    cursor: "grabbing",
  },
};

class MiniMap extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this); // bind all methods to this context
    if (props.componentRef) props.componentRef(this);

    this.state = {
      grabbing: false,
      showHeatmap: localStorage.getItem("showMiniMapHeatmap") === "true",
    };

    this.heatmapCache = {
      key: null,
      buffer: null,
      width: 0,
      height: 0,
    };

    // Add unique identifier for this instance
    this.instanceId = `minimap_${Math.random().toString(36).substring(2, 11)}`;
    this.drawTimeout = null;
    this.isDrawing = false;
    this.scaleFactor = 1; // Default scale factor
    this.offsetX = 0; // Default X offset for rotated states
    this.offsetY = 0; // Default Y offset for rotated states
  }

  componentDidMount() {
    // add zoom with mouse wheel
    this.canvas.addEventListener("mousewheel", this.mousewheel, {
      passive: false,
    });

    // add touch events
    this.canvas.addEventListener("touchstart", this.touchStart, {
      passive: false,
    });
    this.canvas.addEventListener("touchmove", this.touchMove, {
      passive: false,
    });
    this.canvas.addEventListener("touchend", this.touchEnd, { passive: false });

    // get drawing context from canvas
    this.ctx = this.canvas.getContext("2d");

    // extend context with some fancy additional transformation methods
    trackTransforms(this.ctx);

    // converts the relative size css information into an absolute size which we can access
    this.width = this.canvas.width = this.canvas.offsetWidth;
    this.height = this.canvas.height = this.canvas.offsetHeight;

    // get bounds for correct minimap positioning
    let bounds = this.root.getBoundingClientRect();
    if (this.props.inSideBar) {
      bounds.x = 0;
      bounds.y = 0;
    }
    this.setState({
      x: bounds.x,
      y: bounds.y,
    });
  }

  componentWillUnmount() {
    // Clear any pending draw operations
    if (this.drawTimeout) {
      clearTimeout(this.drawTimeout);
    }
    this.canvas.removeEventListener("mousewheel", this.mousewheel);
    this.canvas.removeEventListener("touchstart", this.touchStart);
    this.canvas.removeEventListener("touchmove", this.touchMove);
    this.canvas.removeEventListener("touchend", this.touchEnd);
  }

  /**
   * Toggles the visibility of the heatmap overlay on the minimap.
   * Updates the component state and persists the new value in localStorage.
   * Triggers a redraw of the minimap if not currently drawing.
   */
  toggleHeatmap = () => {
    this.setState(
      (prevState) => {
        const newShowHeatmap = !prevState.showHeatmap;
        localStorage.setItem("showMiniMapHeatmap", newShowHeatmap);
        return { showHeatmap: newShowHeatmap };
      },
      () => {
        // Redraw after toggling
        if (!this.isDrawing) {
          this.draw();
        }
      }
    );
  };

  /**
   * Handles the touch start event on the mini-map.
   * Initiates the dragging state and processes the first touch point.
   *
   * @param {TouchEvent} event - The touch event triggered by the user.
   */
  touchStart(event) {
    this.miniMapMouseEvent(event.touches[0]); // Use the first touch point
    this.dragging = true;
  }

  /**
   * Handles the touch move event on the mini-map.
   * If dragging is active, processes the first touch point
   * to update the mini-map interaction.
   *
   * @param {TouchEvent} event - The touch event triggered by the user.
   */
  touchMove(event) {
    if (this.dragging) {
      this.miniMapMouseEvent(event.touches[0]); // Use the first touch point
    }
  }

  /**
   * Handles the touch end event by setting the dragging state to false.
   * This method is typically used to stop any dragging operation
   * when the user lifts their finger from the touch surface.
   */
  touchEnd() {
    this.dragging = false;
  }

  componentDidUpdate(prevProps) {
    // Only trigger redraw if key props have changed and we're not currently drawing
    const shouldRedraw =
      prevProps.zoom !== this.props.zoom ||
      prevProps.position !== this.props.position ||
      prevProps.rotState !== this.props.rotState ||
      prevProps.mirrored !== this.props.mirrored ||
      prevProps.fileId !== this.props.fileId;

    if (shouldRedraw) {
      // Debounce draw calls to prevent race conditions
      if (this.drawTimeout) {
        clearTimeout(this.drawTimeout);
      }

      // Use requestAnimationFrame instead of setTimeout for better performance
      this.drawTimeout = requestAnimationFrame(() => {
        if (!this.isDrawing) {
          this.draw();
        }
      });
    }
  }

  // zoom out to show the whole image
  zoomOut() {
    // center image first
    let pt = this.ctx.transformedPoint(
      (this.canvas.width - this.vw) / 2,
      (this.canvas.height - this.vh) / 2
    );
    this.ctx.translate(pt.x, pt.y);
    // calc our transformation offset
    pt = this.ctx.transformedPoint(
      this.canvas.width / 2,
      this.canvas.height / 2
    );
    // move to origin
    this.ctx.translate(pt.x, pt.y);
    // calc scale factor so we can fit the whole image on our screen
    var factor = Math.min(
      this.canvas.height / this.vh,
      this.canvas.width / this.vw
    );
    // perform scaling
    this.ctx.scale(factor, factor);
    // restore our offset
    this.ctx.translate(-pt.x, -pt.y);
  }

  isImageBlack(imageElement) {
    try {
      // Create a temporary canvas to analyze the image
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");

      tempCanvas.width = Math.min(imageElement.width, 50); // Sample size for performance
      tempCanvas.height = Math.min(imageElement.height, 50);

      // Draw the image to the temp canvas
      tempCtx.drawImage(
        imageElement,
        0,
        0,
        tempCanvas.width,
        tempCanvas.height,
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );

      // Get image data
      const imageData = tempCtx.getImageData(
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );
      const data = imageData.data;

      // Check if all pixels are black (or very dark)
      let nonBlackPixels = 0;
      const threshold = 10; // Allow for slight variations

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Check if pixel has any significant color or alpha
        if (a > 0 && (r > threshold || g > threshold || b > threshold)) {
          nonBlackPixels++;
          // If we find enough non-black pixels, it's not a black image
          if (nonBlackPixels > 5) {
            return false;
          }
        }
      }

      return true; // Image is essentially black
    } catch (error) {
      console.warn("Error checking if image is black:", error);
      return false; // If we can't check, assume it's not black
    }
  }

  createBlurredCircle(radius, blurPx, color) {
    const size = radius * 2 + blurPx * 4;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.filter = `blur(${blurPx}px)`;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.filter = "none";
    return canvas;
  }

  /**
   * Draws a static, level-dependent heatmap overlay using a grid of dots.
   * Uses a cache so the grid is only recomputed if heatMapTiles grows or settings change.
   * @param {CanvasRenderingContext2D} tempCtx
   */
  drawHeatmapOverlay(tempCtx) {
    if (!this.state.showHeatmap) return;
    if (!this.props.heatMapTiles || !Array.isArray(this.props.heatMapTiles))
      return;

    // --- Configurable dot density ---
    const dotDensity = 24; // Number of dots per axis (increase for higher quality, decrease for speed)
    const colormapType = "jet";
    const colorMapAlpha = 0.5;
    const vw = this.vw;
    const vh = this.vh;

    // --- Cache key: tiles length, dotDensity, vw, vh ---
    const cacheKey = `${this.props.heatMapTiles.length}_${dotDensity}_${vw}_${vh}`;
    let grid, maxTileLevel;

    if (
      this.heatmapCache.key === cacheKey &&
      this.heatmapCache.grid &&
      this.heatmapCache.maxTileLevel
    ) {
      grid = this.heatmapCache.grid;
      maxTileLevel = this.heatmapCache.maxTileLevel;
    } else {
      // --- Prepare grid ---
      grid = [];
      // Precompute tile rectangles and levels for fast lookup
      const tileRects = this.props.heatMapTiles.map((tile) => {
        const tilesPerSide = Math.pow(2, tile.level);
        return {
          x0: (tile.x * vw) / tilesPerSide,
          y0: (tile.y * vh) / tilesPerSide,
          x1: ((tile.x + 1) * vw) / tilesPerSide,
          y1: ((tile.y + 1) * vh) / tilesPerSide,
          level: tile.level,
        };
      });

      // Find the highest tile level present (for normalization)
      maxTileLevel =
        tileRects.length > 0 ? Math.max(...tileRects.map((t) => t.level)) : 1;

      // Build grid and sum levels
      for (let gx = 0; gx < dotDensity; gx++) {
        for (let gy = 0; gy < dotDensity; gy++) {
          // Dot position in image coordinates
          const px = ((gx + 0.5) * vw) / dotDensity;
          const py = ((gy + 0.5) * vh) / dotDensity;
          // Sum levels of tiles containing this dot
          let heat = 0;
          for (let i = 0; i < tileRects.length; i++) {
            const r = tileRects[i];
            if (px >= r.x0 && px < r.x1 && py >= r.y0 && py < r.y1) {
              heat += r.level;
            }
          }
          if (heat > 0) {
            grid.push({ px, py, heat });
          }
        }
      }

      // Store in cache
      this.heatmapCache.key = cacheKey;
      this.heatmapCache.grid = grid;
      this.heatmapCache.maxTileLevel = maxTileLevel;
    }

    // Theoretical maximum heat for a dot: if all levels overlap at one point
    const maxHeat = maxTileLevel * 2; // or just maxTileLevel, or a fixed value

    // --- Draw heatmap dots ---
    tempCtx.save();
    tempCtx.translate(tempCtx.canvas.width / 2, tempCtx.canvas.height / 2);
    tempCtx.scale(this.scaleFactor, this.scaleFactor);
    if (this.props.mirrored) tempCtx.scale(-1, 1);
    tempCtx.rotate(this.props.rotState * (Math.PI / 180));

    for (const dot of grid) {
      // Normalize using fixed maxHeat, not the max found in this grid
      const norm = Math.min(dot.heat / maxHeat, 1.0);
      tempCtx.globalAlpha = colorMapAlpha + 0.5 * norm;
      tempCtx.filter = "blur(6px)";
      tempCtx.beginPath();
      tempCtx.arc(
        dot.px - vw / 2,
        dot.py - vh / 2,
        (Math.max(vw, vh) / dotDensity) * 0.7,
        0,
        2 * Math.PI
      );
      tempCtx.fillStyle = getColorMapColor(colormapType, norm, 1.0);
      tempCtx.fill();
    }
    tempCtx.filter = "none";
    tempCtx.restore();
  }

  draw() {
    // Prevent multiple simultaneous draw operations
    if (this.isDrawing) {
      return;
    }

    this.isDrawing = true;

    try {
      // nothing to draw if we haven't loaded our meta data yet
      if (!this.props.ome || this.canvas === null) {
        this.isDrawing = false;
        return;
      }

      let lv = 0,
        x = 0,
        y = 0;
      let hasValidImage = false;
      let shouldUpdateBackground = false;

      for (let c = 0; c < this.props.histogramConfig.channels.length; c++) {
        let channel = this.props.histogramConfig.channels[c];
        // skip disabled channels
        if (!channel.enabled) continue;

        let page = this.props.getPageForChannel(c)
          ? this.props.getPageForChannel(c)
          : 0;

        let tileId = `${page},${lv},${x},${y},${this.props.fileId}`;
        let img = this.props.tiles.getVisibleImage(tileId);

        if (img) {
          // image is loaded -> check if it's valid
          this.imgLoaded = img.width > 0;
          if (this.imgLoaded) {
            let coloredImg = this.props.tiles.getColoredImage(tileId);

            if (!coloredImg) {
              this.isDrawing = false;
              return;
            }

            // Check if the image is black/empty - only check the raw img, not coloredImg
            if (this.isImageBlack(img)) {
              continue; // Skip this image, try next channel
            }

            hasValidImage = true;
            shouldUpdateBackground = true;

            // Clear and redraw the background image
            var p1 = this.ctx.transformedPoint(0, 0);
            var p2 = this.ctx.transformedPoint(
              this.canvas.width,
              this.canvas.height
            );
            this.ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
            this.ctx.save();
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.restore();

            this.vw = this.props.ome.sizeX;
            this.vh = this.props.ome.sizeY;

            // Create a temporary canvas to avoid context conflicts
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = this.canvas.width;
            tempCanvas.height = this.canvas.height;
            const tempCtx = tempCanvas.getContext("2d");

            // Calculate scaling based on rotation
            if ([90, 270].includes(this.props.rotState)) {
              this.scaleFactor = Math.min(
                tempCanvas.width / this.vh,
                tempCanvas.height / this.vw
              );

              // Calculate and store offsets for rotated states
              let renderedWidth = this.vh * this.scaleFactor;
              let renderedHeight = this.vw * this.scaleFactor;
              this.offsetX = (this.canvas.width - renderedWidth) / 2;
              this.offsetY = (this.canvas.height - renderedHeight) / 2;
            } else {
              this.scaleFactor = Math.min(
                tempCanvas.width / this.vw,
                tempCanvas.height / this.vh
              );

              // For non-rotated states, offsets are 0
              this.offsetX = 0;
              this.offsetY = 0;
            }

            tempCtx.save();
            tempCtx.setTransform(1, 0, 0, 1, 0, 0);
            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

            // Center the image
            tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tempCtx.scale(this.scaleFactor, this.scaleFactor);

            if (this.props.mirrored) {
              tempCtx.scale(-1, 1);
            }

            const angleInRadians = this.props.rotState * (Math.PI / 180);
            tempCtx.rotate(angleInRadians);
            tempCtx.drawImage(
              coloredImg,
              -this.vw / 2,
              -this.vh / 2,
              this.vw,
              this.vh
            );

            tempCtx.restore();

            // Draw heatmap overlay
            this.drawHeatmapOverlay(tempCtx);

            // Copy from temp canvas to main canvas
            this.ctx.save();
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.ctx.restore();

            // Copy from temp canvas to main canvas
            this.ctx.save();
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.ctx.restore();

            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
            this.zoomOut();

            break; // Found valid image, no need to check other channels
          }
        }
      }

      // Always ensure we have dimensions set for drawing viewport rectangle
      if (!this.vw && this.props.ome) {
        this.vw = this.props.ome.sizeX;
        this.vh = this.props.ome.sizeY;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.zoomOut();
      }

      // Always draw the viewport rectangle if we have dimensions (even with previous background)
      if (this.vw) {
        this.drawViewportRectangle();
      }

      if (!shouldUpdateBackground && !hasValidImage) {
        console.log(
          `MiniMap: No valid image available for fileId ${this.props.fileId}, keeping previous background but updating viewport rectangle`
        );
      }
    } catch (error) {
      console.error("MiniMap draw error:", error);
    } finally {
      this.isDrawing = false;
    }
  }

  // Extract viewport rectangle drawing to separate method
  drawViewportRectangle() {
    let mainCanvas = this.props.canvas;

    let screenX = this.props.position.x / this.props.zoom;
    let screenY = this.props.position.y / this.props.zoom;
    let screenW = mainCanvas.width / this.props.zoom;
    let screenH = mainCanvas.height / this.props.zoom;

    // ...existing viewport rectangle code...
    // (keeping the existing rotation and drawing logic)

    // Adjust for rotation
    if ([90, 270].includes(this.props.rotState)) {
      [screenW, screenH] = [screenH, screenW];
      const scaleAdjustment = Math.min(
        this.canvas.width / this.vh,
        this.canvas.height / this.vw
      );
      const adjustedZoom = this.props.zoom - this.props.zoom * scaleAdjustment;
      screenX -= (screenW - mainCanvas.width / adjustedZoom) / 2;
      screenY -= (screenH - mainCanvas.height / adjustedZoom) / 2;
      const previewOffsetX = (this.vh - this.vw) / 2;
      const previewOffsetY = (this.vw - this.vh) / 2;
      screenX -= previewOffsetX;
      screenY -= previewOffsetY;
    }

    let offsetX = -this.ctx.getTransform().e / this.ctx.getTransform().a;
    let offsetY = -this.ctx.getTransform().f / this.ctx.getTransform().a;

    this.ctx.globalCompositeOperation = "source-over";
    this.ctx.lineWidth = 2 / this.ctx.getTransform().a;
    this.ctx.strokeStyle = this.props.theme.palette.primary.main;
    this.ctx.beginPath();
    this.ctx.rect(screenX, screenY, screenW, screenH);
    this.ctx.moveTo(screenX + screenW / 2, screenY);
    this.ctx.lineTo(screenX + screenW / 2, offsetY);
    this.ctx.moveTo(screenX + screenW / 2, screenY + screenH);
    this.ctx.lineTo(
      screenX + screenW / 2,
      this.canvas.height / this.ctx.getTransform().a
    );
    this.ctx.moveTo(offsetX, screenY + screenH / 2);
    this.ctx.lineTo(screenX, screenY + screenH / 2);
    this.ctx.moveTo(screenX + screenW, screenY + screenH / 2);
    this.ctx.lineTo(
      this.canvas.width / this.ctx.getTransform().a,
      screenY + screenH / 2
    );
    this.ctx.stroke();
  }

  // returns the current zoom scale
  getScale() {
    return this.ctx.getTransform().a;
  }

  // returns position of our image
  getPosition() {
    return {
      x: -this.ctx.getTransform().e,
      y: -this.ctx.getTransform().f,
    };
  }

  /**
   * Returns the current canvas rendering context.
   *
   * @returns {CanvasRenderingContext2D|null} The current canvas context, or null if not set.
   */
  getCtx() {
    let c = this.ctx;
    return c;
  }

  /**
   * Handles mouse events on the minimap, transforming screen coordinates to image coordinates
   * depending on the current rotation state. For 90/270 degree rotations, adjusts for minimap
   * centering and scaling. For other rotations, uses the canvas context's transformedPoint method.
   *
   * @param {MouseEvent} event - The mouse event triggered on the minimap.
   */
  miniMapMouseEvent(event) {
    // transform mouse coordinates on screen to relative to minimap component coordinates
    let x = event.pageX - this.state.x;
    let y = event.pageY - this.state.y;

    if (this.props.rotState === 90 || this.props.rotState === 270) {
      // Remove minimap centering offset
      x = x - this.offsetX;
      y = y - this.offsetY;

      // Map minimap pixel to image coordinates
      // For 90/270, scaleFactor is based on vh/vw swapped
      const imageX = x / this.scaleFactor;
      const imageY = y / this.scaleFactor;

      this.props.onMoveTo({ x: imageX, y: imageY });
      this.props.chainMouseMove(x, y, event);
      return;
    }

    // Non-rotated: use the transform as before
    let pt = this.ctx.transformedPoint(x, y);
    this.props.onMoveTo({ x: pt.x, y: pt.y });
    this.props.chainMouseMove(x, y, event);
  }

  // handle dragging of the minimap region rectangle
  mouseDown(event) {
    this.miniMapMouseEvent(event);
    this.dragging = true;
  }

  // handle dragging of the minimap region rectangle
  mouseMove(event) {
    if (this.dragging) {
      this.miniMapMouseEvent(event);
    }
  }

  mouseUp() {
    this.dragging = false;
  }

  // zoom event in minimap
  mousewheel(event) {
    let x = event.pageX - this.state.x;
    let y = event.pageY - this.state.y;
    this.props.zoomMouseWheel(event, x, y);
  }

  // drag stop event of minimap
  stopEvent() {
    let bounds = this.root.getBoundingClientRect();
    if (this.props.inSideBar) {
      bounds.x = 0;
      bounds.y = 0;
    }
    this.setState({ grabbing: false, x: bounds.x, y: bounds.y });
  }

  handleDragStart() {
    this.setState({ grabbing: true });
  }

  setRootRef(element) {
    this.root = element;
  }

  setCanvasRef(element) {
    this.canvas = element;
  }

  render() {
    const { classes, pointerEvents } = this.props;
    const { grabbing, showHeatmap } = this.state;

    let classToUse = this.props.inSideBar ? classes.rootsideBar : classes.root;

    return (
      <Draggable
        handle=".handle"
        onStart={this.handleDragStart}
        onStop={this.stopEvent}
      >
        <div
          ref={this.setRootRef}
          className={classNames(classToUse, grabbing && classes.grabbing)}
          style={{
            pointerEvents: pointerEvents ? "all" : "none",
            top: this.props.canvas.height - 175,
            zIndex: 9999,
          }}
        >
          <IconButton
            size="small"
            style={{
              position: "absolute",
              top: 5,
              left: 5,
              zIndex: 10001,
              background: "#fff",
            }}
            onClick={this.toggleHeatmap}
            aria-label="Toggle heatmap"
          >
            <HSAIcon icon="HeatmapTool" active={showHeatmap} size={18} />
          </IconButton>
          <canvas
            ref={this.setCanvasRef}
            className={classes.canvas}
            onMouseDown={this.mouseDown}
            onMouseMove={this.mouseMove}
            onMouseUp={this.mouseUp}
          />
          {!this.props.inSideBar && (
            <DragIndicator
              style={{ zIndex: 10000 }}
              className={classNames(
                "handle",
                classes.dragIndicator,
                grabbing && classes.grabbing
              )}
            />
          )}
        </div>
      </Draggable>
    );
  }
}

// define the component's interface
MiniMap.propTypes = {
  canvas: PropTypes.object,
  chainMouseMove: PropTypes.func,
  classes: PropTypes.object.isRequired,
  componentRef: PropTypes.func,
  histogramConfig: PropTypes.object,
  inSideBar: PropTypes.bool,
  ome: PropTypes.object,
  onMoveTo: PropTypes.func,
  onScaleOnly: PropTypes.func,
  pointerEvents: PropTypes.bool,
  position: PropTypes.object,
  theme: PropTypes.object,
  zoom: PropTypes.number,
  zoomMouseWheel: PropTypes.func,
  // from withTiles
  fileId: PropTypes.string,
  getPageForChannel: PropTypes.func,
  mirrored: PropTypes.bool,
  rotState: PropTypes.number,
  tiles: PropTypes.object,
  heatMapTiles: PropTypes.array, // Heatmap tiles from Renderer
};

export default withTiles(withTheme(withStyles(styles)(MiniMap)));
